// Phone numbers and email addresses wherever they are displayed.
//  - a number: long-press (or right-click on a desktop) opens Call / WhatsApp;
//  - an address: tapping it opens the mail app.
// The stored strings can hold several values ("+91 …, +91 …"); the actions use the first one,
// while the text shows everything on record.

import { useRef, useState } from "react";
import { MessageCircle, Phone } from "lucide-react";

import { emailAddress, telNumber, whatsappNumber } from "@/lib/contact";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LONG_PRESS_MS = 450;

export function PhoneValue({ value, className }: { value?: string | null; className?: string }) {
  const [open, setOpen] = useState(false);
  const timer = useRef<number | null>(null);
  const tel = telNumber(value);
  const wa = whatsappNumber(value);

  if (!value) return <span className={className}>—</span>;
  if (!tel && !wa) return <span className={className}>{value}</span>;

  const cancel = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        className={cn("text-left underline decoration-dotted underline-offset-4 outline-none", className)}
        title="Long press for Call / WhatsApp"
        // Long press on touch, right-click on a desktop.
        onPointerDown={() => {
          cancel();
          timer.current = window.setTimeout(() => setOpen(true), LONG_PRESS_MS);
        }}
        onPointerUp={cancel}
        onPointerLeave={cancel}
        onPointerCancel={cancel}
        onContextMenu={(e) => {
          e.preventDefault();
          cancel();
          setOpen(true);
        }}
      >
        {value}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        {tel && (
          <DropdownMenuItem render={<a href={`tel:${tel}`} />}>
            <Phone /> Call
          </DropdownMenuItem>
        )}
        {wa && (
          <DropdownMenuItem render={<a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" />}>
            <MessageCircle /> WhatsApp
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function EmailValue({ value, className }: { value?: string | null; className?: string }) {
  const mail = emailAddress(value);
  if (!value) return <span className={className}>—</span>;
  if (!mail) return <span className={className}>{value}</span>;
  return (
    <a
      href={`mailto:${mail}`}
      className={cn("underline decoration-dotted underline-offset-4 hover:text-primary", className)}
      title={`Email ${mail}`}
    >
      {value}
    </a>
  );
}
